import { DEFAULT_FRAME } from './frames'
import { personaStorageKey } from './persona'
import { FrameId, Op } from './pipeline-types'
import { SavedView, ViewConfig } from './view-config'

/**
 * localStorage persistence for user-saved view configs.
 * Prototype-only: the production version is a saved-views table keyed by org.
 */

export const STORAGE_KEY = 'reporting-prototype.savedViews'

/** Shape gate for deserialized entries — corrupt/foreign entries are dropped silently. */
const isSavedView = (v: unknown): v is SavedView => {
  if (typeof v !== 'object' || v === null) return false
  const c = v as Record<string, unknown>
  return (
    typeof c.id === 'string' &&
    typeof c.name === 'string' &&
    (c.kind === 'aggregate' || c.kind === 'list') &&
    Array.isArray(c.filters) &&
    typeof c.chart === 'string' &&
    Array.isArray(c.knobs) &&
    typeof c.basedOn === 'string'
  )
}

export function listSavedViews(): SavedView[] {
  try {
    const raw = localStorage.getItem(personaStorageKey(STORAGE_KEY))
    if (raw === null) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isSavedView)
  } catch {
    return []
  }
}

export function saveView(config: ViewConfig, name: string): SavedView {
  const saved: SavedView = {
    ...config,
    id: `saved-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    basedOn: 'basedOn' in config ? (config as SavedView).basedOn : config.id,
    createdAt: new Date().toISOString(),
  }
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify([...listSavedViews(), saved])
  )
  return saved
}

/**
 * Persist a pipeline-built report. The op-chain is the source of truth; the
 * surrounding fields are a display shell so it lists as an ordinary library
 * card and passes the same shape gate as ViewConfig-based saved views.
 */
export function savePipelineView(args: {
  name: string
  description?: string
  ops: Op[]
  frame?: FrameId
}): SavedView {
  const { name, description = '', ops, frame = DEFAULT_FRAME } = args
  const shell: SavedView = {
    id: `saved-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    description,
    kind: 'aggregate',
    icon: 'chart',
    filters: [],
    chart: 'table-only',
    knobs: [],
    basedOn: 'pipeline',
    createdAt: new Date().toISOString(),
    pipeline: ops,
    pipelineFrame: frame,
  }
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify([...listSavedViews(), shell])
  )
  return shell
}

/**
 * Overwrite an existing saved view in place (keeps its id, basedOn and
 * createdAt). Returns the updated view, or undefined if no saved view has that
 * id (e.g. trying to overwrite a shipped template — callers should "save as a
 * copy" instead). Mirrors updateSavedDashboard so reports and dashboards behave
 * the same.
 */
export function updateSavedView(
  id: string,
  config: ViewConfig,
  name: string
): SavedView | undefined {
  const list = listSavedViews()
  const existing = list.find((v) => v.id === id)
  if (!existing) return undefined
  const updated: SavedView = {
    ...config,
    id: existing.id,
    name,
    basedOn: existing.basedOn,
    createdAt: existing.createdAt,
  }
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify(list.map((v) => (v.id === id ? updated : v)))
  )
  return updated
}

/**
 * Overwrite a pipeline-built report in place: rename it and/or persist an edited
 * op-chain, keeping its id/basedOn/createdAt and display shell. The pipeline
 * counterpart to updateSavedView (which spreads a ViewConfig); this preserves
 * the pipeline fields a ViewConfig spread would drop. Returns undefined when no
 * saved view has that id.
 */
export function updatePipelineView(
  id: string,
  args: { name: string; description?: string; ops: Op[]; frame?: FrameId }
): SavedView | undefined {
  const list = listSavedViews()
  const existing = list.find((v) => v.id === id)
  if (!existing) return undefined
  const updated: SavedView = {
    ...existing,
    name: args.name,
    description: args.description ?? existing.description,
    pipeline: args.ops,
    pipelineFrame: args.frame ?? existing.pipelineFrame,
  }
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify(list.map((v) => (v.id === id ? updated : v)))
  )
  return updated
}

export function deleteSavedView(id: string): void {
  localStorage.setItem(
    personaStorageKey(STORAGE_KEY),
    JSON.stringify(listSavedViews().filter((v) => v.id !== id))
  )
}

export const getSavedView = (id: string): SavedView | undefined =>
  listSavedViews().find((v) => v.id === id)
